// HeroSection.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CONFIG from "../config";

// ✅ Now API ONLY from CONFIG (no fallback)
// Make sure in config.js you have:  PRODUCTS_API: "https://.../products"
const PRODUCTS_API = CONFIG.PRODUCTS_API;

/**
 * ================================
 * CATEGORY SEARCH ENGINE (FRONTEND)
 * ================================
 *
 * Goal:
 *  - User can type messy queries like:
 *    - "books", "buk", "story book", "swap books near me"
 *    - "sport good", "sports item", "cricket stuff"
 *    - "kids toys", "toy & game", "toys for kids"
 *  - We still detect correct category & route:
 *      /products?category=<Canonical Name>
 *
 * Approach:
 *  1. Normalize text → lowercase, remove punctuation, trim.
 *  2. Tokenize → words array.
 *  3. Category definitions with synonyms, keywords, and weight.
 *  4. Fuzzy matching using Levenshtein distance.
 *  5. Score per category:
 *      - Exact phrase match
 *      - Token matches
 *      - Synonym matches
 *      - Fuzzy similarity
 */

// Canonical categories used across the app
const CATEGORY_DEFINITIONS = [
  {
    key: "Toys & Games",
    canonical: "Toys & Games",
    searchTerms: [
      "toy", "toys", "game", "games", "toys & games", "toys and games",
      "kids toys", "children toys", "board game", "board games", "puzzle",
      "lego", "play", "kid stuff", "kid items", "gaming toy","bike",

      // ✅ added
      "remote car", "rc car", "battery toy", "plastic toy", "soft toy",
      "stuffed toy", "teddy bear", "action figure", "barbie", "doll",
      "robot toy", "rubik cube", "jigsaw puzzle", "uno cards",
      "building blocks", "nerf gun", "slime", "play doh", "water gun",
      "dart game", "marbles", "toy train", "toy car", "toy bike",
      "race track", "kids game", "outdoor toys", "indoor game",
      "chess", "ludo", "carrom", "snake and ladder",
      "cards game", "educational toy", "learning toy",
      "kids puzzle", "children game", "pretend play",
      "kitchen set toy", "doctor set toy", "toy animal",
      "toy dinosaur", "toy vehicle", "toy robot", "puppet",
      "toy blocks", "stacking toy", "baby toy", "toddler toy",
      "skill game", "strategy game", "science kit", "magic set",
      "bubble toy", "kite", "yo yo", "frisbee"
    ],
  },

  {
    key: "Sporting Goods",
    canonical: "Sporting Goods",
    searchTerms: [
      "sporting goods", "sports", "sport", "sports items", "sports gear",
      "cricket", "football", "soccer", "bat", "ball", "racket", "rackets",
      "badminton", "tennis", "gym equipment", "fitness",

      // ✅ added
      "basketball", "volleyball", "hockey stick", "table tennis",
      "ping pong", "shuttlecock", "dumbbell", "kettlebell", "treadmill",
      "cycling", "bicycle helmet", "boxing gloves", "sports shoes",
      "skipping rope", "yoga mat", "gym belt", "protein shaker",
      "sports bag", "swimming goggles", "cricket pads", "cricket gloves",
      "football studs", "sports cap", "arm guard", "knee guard",
      "elbow guard", "wrist band", "headband", "sports jersey",
      "sports t shirt", "goalkeeper gloves", "tennis balls",
      "basketball hoop", "sports net", "carrom board",
      "pool cue", "skateboard", "roller skates", "sports bottle",
      "resistance band", "gym gloves", "weight plate", "barbell",
      "punching bag", "sports kit", "running gear"
    ],
  },

  {
    key: "Accessories",
    canonical: "Accessories",
    searchTerms: [
      "accessory", "accessories", "jewellery", "jewelry", "bracelet",
      "bracelets", "necklace", "pendant", "watch", "watches", "sunglass",
      "sunglasses", "belt", "belts", "wallet", "wallets",

      // ✅ added
      "earrings", "ring", "rings", "anklet", "nose pin", "brooch",
      "hair clip", "hairband", "scrunchie", "scarf", "cap", "hat",
      "beanie", "gloves", "keychain", "tie", "bow tie", "cufflinks",
      "chain", "locket", "studs", "bracelet set", "handbag charm",
      "pouch", "travel wallet", "card holder", "coin purse",
      "sling accessory", "goggles", "aviators", "fashion jewelry",
      "smart watch", "analog watch", "digital watch", "watch strap",
      "belt buckle", "wallet chain", "hair accessory", "fashion belt",
      "gents wallet", "ladies wallet"
    ],
  },

  {
    key: "Books",
    canonical: "Books",
    searchTerms: [
      "book", "books", "novel", "novels", "story book", "story books",
      "textbook", "textbooks", "manga", "comics", "comic book",
      "reading", "literature", "study material",

      // ✅ added
      "children book", "fairy tale", "reference book", "encyclopedia",
      "biography", "autobiography", "fiction", "non fiction",
      "self help", "motivational book", "poetry", "workbook",
      "exam guide", "sample papers", "school book", "college book",
      "educational book", "dictionary", "atlas", "novella",
      "graphic novel", "science book", "history book", "maths book",
      "story novel", "short stories", "classic novel", "romance novel",
      "thriller book", "mystery book", "crime novel", "spiritual book",
      "religious book", "language book"
    ],
  },

  {
    key: "Clothes",
    canonical: "Clothes",
    searchTerms: [
      "cloth", "clothes", "clothing", "t-shirt", "t shirt", "shirt",
      "shirts", "pant", "pants", "jeans", "dress", "dresses", "kurti",
      "kurta", "saree", "hoodie", "jacket", "sweater", "top", "tops",
      "skirt",

      // ✅ added
      "salwar", "salwar suit", "lehenga", "anarkali", "blouse",
      "tunic", "shrug", "cardigan", "coat", "blazer", "tracksuit",
      "track pants", "shorts", "cargo pants", "leggings", "palazzo",
      "dupatta", "gown", "party wear", "crop top", "tank top",
      "sportswear", "nightwear", "pajamas", "innerwear", "vest",
      "socks", "thermal", "raincoat", "windcheater", "denim jacket",
      "printed t shirt", "formal shirt", "formal pants", "ethnic wear",
      "kids clothes", "baby clothes"
    ],
  },

  {
    key: "Shoes",
    canonical: "Shoes",
    searchTerms: [
      "shoe", "shoes", "footwear", "sneaker", "sneakers", "boots",
      "heels", "sandals", "slippers", "flip flop", "flip flops",
      "running shoes", "sports shoes",

      // ✅ added
      "formal shoes", "casual shoes", "canvas shoes", "loafer",
      "loafers", "moccasin", "wedges", "platform heels", "ballerina",
      "flat sandals", "crocs", "clogs", "ankle boots", "long boots",
      "kids shoes", "baby shoes", "school shoes", "leather shoes",
      "trekking shoes", "hiking shoes", "football studs", "cricket spikes",
      "mens shoes", "womens shoes", "slip ons", "shoe lace"
    ],
  },

  {
    key: "Bags",
    canonical: "Bags",
    searchTerms: [
      "bag", "bags", "handbag", "hand bag", "backpack", "backpacks",
      "school bag", "laptop bag", "duffel bag", "travel bag", "purse",
      "tote bag",

      // ✅ added
      "sling bag", "messenger bag", "shoulder bag", "clutch",
      "wallet", "coin pouch", "gym bag", "hand luggage", "suitcase",
      "carry on", "briefcase", "camera bag", "side bag",
      "kids bag", "baby diaper bag", "shopping bag", "paper bag",
      "jute bag", "office bag", "file bag", "laptop sleeve",
      "drawstring bag", "bucket bag", "crossbody bag"
    ],
  },
];

/**
 * Normalize a text:
 *  - lowercase
 *  - remove punctuation
 *  - collapse spaces
 */
function normalizeText(text = "") {
  return text
    .toString()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s&]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Split normalized string into tokens (words).
 */
function tokenize(text = "") {
  const normalized = normalizeText(text);
  if (!normalized) return [];
  return normalized.split(" ").filter(Boolean);
}

/**
 * Levenshtein distance for fuzzy matching.
 */
function levenshteinDistance(a = "", b = "") {
  const s = a.toLowerCase();
  const t = b.toLowerCase();

  if (!s.length) return t.length;
  if (!t.length) return s.length;

  const dp = Array.from({ length: s.length + 1 }, () =>
    new Array(t.length + 1).fill(0)
  );

  for (let i = 0; i <= s.length; i++) dp[i][0] = i;
  for (let j = 0; j <= t.length; j++) dp[0][j] = j;

  for (let i = 1; i <= s.length; i++) {
    for (let j = 1; j <= t.length; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[s.length][t.length];
}

/**
 * Similarity between 0 and 1 (1 = exact match)
 */
function similarityScore(a = "", b = "") {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const dist = levenshteinDistance(a, b);
  const maxLen = Math.max(a.length, b.length) || 1;
  return 1 - dist / maxLen;
}

/**
 * Detect category from free text query.
 */
function detectCategoryFromQuery(rawQuery) {
  const query = normalizeText(rawQuery);
  if (!query) return null;

  const tokens = tokenize(query);

  let bestMatch = null;

  for (const category of CATEGORY_DEFINITIONS) {
    let score = 0;
    const debug = {
      key: category.key,
      canonical: category.canonical,
      directPhraseHits: [],
      tokenHits: [],
      synonymHits: [],
      fuzzyHits: [],
    };

    const catNameNorm = normalizeText(category.canonical);

    // 1) Direct phrase match
    if (query === catNameNorm) {
      score += 8;
      debug.directPhraseHits.push(catNameNorm);
    } else if (query.includes(catNameNorm)) {
      score += 5;
      debug.directPhraseHits.push(catNameNorm);
    }

    // 2) Token presence + synonyms + fuzzy
    for (const token of tokens) {
      if (!token) continue;

      if (catNameNorm.includes(token)) {
        score += 2;
        debug.tokenHits.push(token);
      }

      for (const term of category.searchTerms) {
        const termNorm = normalizeText(term);
        if (!termNorm) continue;

        if (termNorm === token || termNorm === query) {
          score += 3;
          debug.synonymHits.push({ token, term: termNorm, type: "exact" });
        } else if (termNorm.includes(token) || token.includes(termNorm)) {
          score += 2;
          debug.synonymHits.push({ token, term: termNorm, type: "contains" });
        } else {
          const sim = similarityScore(token, termNorm);
          if (sim >= 0.78) {
            score += 1.4;
            debug.fuzzyHits.push({ token, term: termNorm, sim });
          } else if (sim >= 0.68) {
            score += 0.7;
            debug.fuzzyHits.push({ token, term: termNorm, sim });
          }
        }
      }
    }

    // 5) Bonus if query has "swap", "buy", etc.
    const tradeWords = ["swap", "trade", "sell", "buy", "exchange", "barter"];
    for (const t of tradeWords) {
      if (query.includes(t) && catNameNorm.split(" ").some((w) => query.includes(w))) {
        score += 1.5;
      }
    }

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = {
        category: category.canonical,
        key: category.key,
        score,
        debug,
      };
    }
  }

  if (!bestMatch || bestMatch.score < 2.5) {
    return null;
  }

  return bestMatch;
}

/**
 * Product search filter (name / desc / category includes query)
 */
function filterProductsByText(products, query) {
  const q = normalizeText(query);
  if (!q) return [];
  return products.filter((p) => {
    const name = normalizeText(p.name || "");
    const desc = normalizeText(p.description || "");
    const cat = normalizeText(p.category || "");
    return name.includes(q) || desc.includes(q) || cat.includes(q);
  });
}

export default function HeroSection() {
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [locationText, setLocationText] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();

    const rawQuery = searchText.trim();

    // If user didn't type anything → open products
    if (!rawQuery) {
      navigate("/products");
      return;
    }

    // STEP 1: Try to detect category
    const catMatch = detectCategoryFromQuery(rawQuery);

    if (catMatch && catMatch.category) {
      navigate(`/products?category=${encodeURIComponent(catMatch.category)}`);
      return;
    }

    // STEP 2: Fallback to product text search
    try {
      // ✅ Now this URL comes only from CONFIG.PRODUCTS_API
      const res = await axios.get(PRODUCTS_API);
      const products = res.data?.products || [];

      const accepted = products.filter(
        (p) => (p.approval_status || "").toLowerCase() === "accepted"
      );

      const matchedProducts = filterProductsByText(accepted, rawQuery);

      if (matchedProducts.length > 0) {
        navigate("/products", {
          state: {
            searchResults: matchedProducts,
            heroSearch: rawQuery,
          },
        });
      } else {
        navigate("/products");
      }
    } catch (error) {
      console.error("Search error:", error);
      navigate("/products");
    }
  };

  return (
    <section style={heroStyles.hero}>
      <div style={heroStyles.overlay}></div>

      <div style={heroStyles.heroContainer}>
        <div style={heroStyles.heroContent}>
          <div style={heroStyles.left}>
            <p style={heroStyles.smallTagline}>India&apos;s Best online</p>

            <h1 style={heroStyles.mainHeading}>Barter Marketplace</h1>

            <p style={heroStyles.subHeading}>
              "Post what you have, find what you need, and trade with ease!"
            </p>

            <form style={heroStyles.searchBar} onSubmit={handleSearch}>
              <div style={heroStyles.searchField}>
                <span style={heroStyles.fieldIcon}>🔍</span>
                <input
                  style={heroStyles.searchInput}
                  placeholder="Find Categories Products........."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <div style={heroStyles.searchField}>
                <span style={heroStyles.fieldIcon}>📍</span>
                <input
                  style={heroStyles.searchInput}
                  placeholder="Location"
                  value={locationText}
                  onChange={(e) => setLocationText(e.target.value)}
                />
              </div>

              <button type="submit" style={heroStyles.searchButton}>
                Search
              </button>
            </form>

            <p style={heroStyles.popular}>
              <strong>What&apos;s popular:</strong> Sporting Goods, Toys &amp;
              Games, Accessories, Books, Clothes, Shoes, Bags
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ❗ DESIGN IS UNTOUCHED BELOW ❗ */
const heroStyles = {
  hero: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    backgroundImage: `
      linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)),
      url("https://demo.swapiify.com/uploads/products/0001.png")
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },

  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.15)",
    zIndex: 1,
  },

  heroContainer: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 40px",
    position: "relative",
    zIndex: 2,
  },

  heroContent: {
    display: "flex",
    alignItems: "center",
  },

  left: {
    maxWidth: "600px",
    color: "#ffffff",
  },

  smallTagline: {
    fontSize: "18px",
    marginBottom: "10px",
    opacity: 0.9,
  },

  mainHeading: {
    fontSize: "3.5rem",
    fontWeight: "800",
    lineHeight: "1.1",
    marginBottom: "15px",
    color: "#ffffff",
  },

  subHeading: {
    fontSize: "1.1rem",
    marginBottom: "30px",
    opacity: 0.9,
  },

  searchBar: {
    display: "flex",
    alignItems: "center",
    background: "rgba(255,255,255,0.95)",
    borderRadius: "60px",
    padding: "8px",
    gap: "10px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
  },

  searchField: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    background: "#f3f3f3",
    padding: "10px 16px",
    borderRadius: "40px",
    gap: "8px",
  },

  fieldIcon: {
    fontSize: "16px",
  },

  searchInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    width: "100%",
    fontSize: "14px",
  },

  searchButton: {
    background: "#FF7F50",
    color: "#ffffff",
    border: "none",
    padding: "12px 26px",
    borderRadius: "40px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  popular: {
    marginTop: "15px",
    fontSize: "14px",
    opacity: 0.95,
  },
};
